import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listAssignableSalesTeams } from '@/services/salesTeam.service';
import {
  useLeads,
  useDeleteLead,
  useUpdateLeadStatus,
  patchLeadStatusInListCache,
  type Lead,
} from '@/hooks/useLeads';
import { useLeadsBoardFilters } from '@/hooks/useLeadsBoardFilters';
import { LeadsPageHeader } from '@/components/crm/leads/LeadsPageHeader';
import { LeadsKpiStrip } from '@/components/crm/leads/LeadsKpiStrip';
import { LeadsFilterBar } from '@/components/crm/leads/LeadsFilterBar';
import { LeadsKanbanBoard } from '@/components/crm/leads/LeadsKanbanBoard';
import { LeadsTableView } from '@/components/crm/leads/LeadsTableView';
import { LeadStatusReasonModal } from '@/components/crm/leads/LeadStatusReasonModal';
import { LEAD_CLOSED_STATUSES, type LeadStatusReasonInput } from '@/services/lead.service';
import { useInstalledSolutionPacks } from '@/hooks/useInstalledSolutionPacks';
import { EPC_COMMERCIAL_PACK_KEYS } from '@/config/solutionPackNavGates';

export const LeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [updatingLeadId, setUpdatingLeadId] = useState<string | null>(null);
  const [pendingClose, setPendingClose] = useState<{ leadId: string; status: string; previous?: string } | null>(null);

  const filters = useLeadsBoardFilters();
  const { installedPackKeys } = useInstalledSolutionPacks();
  const epcCommercialEnabled =
    installedPackKeys != null &&
    EPC_COMMERCIAL_PACK_KEYS.some((k) => installedPackKeys.includes(k));
  const { data: salesTeams = [] } = useQuery({
    queryKey: ['sales-teams-assignable'],
    queryFn: listAssignableSalesTeams,
  });

  const { data: leadsData = [], isLoading } = useLeads(filters.apiFilters);
  const deleteLead = useDeleteLead();
  const updateLeadStatus = useUpdateLeadStatus();

  const leads = useMemo(() => {
    if (filters.assignScope !== 'unassigned') return leadsData;
    return leadsData.filter((l) => !l.assigned_to);
  }, [leadsData, filters.assignScope]);

  const openCreate = () => navigate('/growth/leads/new');

  const openEdit = (lead: Lead, e?: React.MouseEvent) => {
    e?.stopPropagation();
    navigate(`/growth/leads/${lead.id}/edit`);
  };

  const openView = (lead: Lead) => navigate(`/growth/leads/${lead.id}`);

  const handleDelete = (lead: Lead, e?: React.MouseEvent) => {
    e?.stopPropagation();
    Modal.confirm({
      title: 'Delete Lead',
      content: `Delete lead "${lead.customer_name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deleteLead.mutateAsync(lead.id),
    });
  };

  const changeStatus = (leadId: string, newStatus: string, previousStatus?: string, reason?: LeadStatusReasonInput) => {
    if (!reason && LEAD_CLOSED_STATUSES.includes(newStatus)) {
      setPendingClose({ leadId, status: newStatus, previous: previousStatus });
      return;
    }
    const prev = previousStatus;
    patchLeadStatusInListCache(queryClient, filters.apiFilters, leadId, newStatus);
    setUpdatingLeadId(leadId);
    updateLeadStatus.mutate(
      { id: leadId, status: newStatus, reason },
      {
        onError: () => {
          if (prev) patchLeadStatusInListCache(queryClient, filters.apiFilters, leadId, prev);
        },
        onSettled: () => setUpdatingLeadId(null),
      }
    );
  };

  const handleDragStatusChange = (leadId: string, newStatus: string) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.lead_status === newStatus) return;
    changeStatus(leadId, newStatus, lead.lead_status);
  };

  const handleMoveStatus = (lead: Lead, status: string) => {
    if (lead.lead_status === status) return;
    changeStatus(lead.id, status, lead.lead_status);
  };

  return (
    <div style={{ padding: '0 0 24px' }}>
      <LeadsPageHeader onAddLead={openCreate} />
      <LeadsKpiStrip leads={leads} />
      <LeadsFilterBar
        searchInput={filters.searchInput}
        onSearchChange={filters.setSearchInput}
        priorityFilter={filters.priorityFilter}
        onPriorityChange={filters.setPriorityFilter}
        assignScope={filters.assignScope}
        onAssignScopeChange={filters.setAssignScope}
        teamFilter={filters.teamFilter}
        missingTeamOnly={filters.missingTeamOnly}
        onTeamChange={(teamId, missingOnly) => {
          filters.setTeamFilter(teamId || '');
          filters.setMissingTeamOnly(missingOnly);
        }}
        salesTeams={salesTeams}
        viewMode={filters.viewMode}
        onViewModeChange={filters.setViewMode}
        hasActiveFilters={filters.hasActiveFilters}
        onClearFilters={filters.clearFilters}
      />

      {filters.viewMode === 'kanban' ? (
        <LeadsKanbanBoard
          leads={leads}
          isLoading={isLoading}
          updatingLeadId={updatingLeadId}
          onDragStatusChange={handleDragStatusChange}
          onView={openView}
          onEdit={openEdit}
          onDelete={handleDelete}
          onMoveStatus={handleMoveStatus}
          onAddLead={openCreate}
        />
      ) : (
        <LeadsTableView
          leads={leads}
          isLoading={isLoading}
          epcMode={epcCommercialEnabled}
          onView={openView}
          onEdit={(lead) => openEdit(lead)}
        />
      )}
      <LeadStatusReasonModal
        status={pendingClose?.status ?? null}
        leadName={leads.find((l) => l.id === pendingClose?.leadId)?.customer_name}
        onCancel={() => setPendingClose(null)}
        onSubmit={(reason) => {
          if (pendingClose) changeStatus(pendingClose.leadId, pendingClose.status, pendingClose.previous, reason);
          setPendingClose(null);
        }}
      />
    </div>
  );
};
